export class FinCalc {

  /**
   * MOIC - Multiple on Invested Capital
   *
   * Calculated as fairValue / cost if cost is available
   */
  static moic(_fairValue: number, _cost: number): number {
    const fairValue = Number(_fairValue ?? 0);
    const cost = Number(_cost ?? 0);
    return cost ? fairValue / cost : 0;
  }

  /**
   * Gain is equal to the fairValue minus the cost price.
   * If value is negative, usually named as Loss
   */
  static gain(_fairValue: number, _cost: number): number {
    const fairValue = Number(_fairValue ?? 0);
    const cost = Number(_cost ?? 0);
    return fairValue - cost;
  }

  /**
   * It is the difference between the gross gain or loss and the total indirect income/expenses of a business.
   */
  static netGain(_fairValue: number, _cost: number, _expenses: number): number {
    const fairValue = Number(_fairValue ?? 0);
    const cost = Number(_cost ?? 0);
    const expenses = Number(_expenses ?? 0);
    return fairValue - cost + expenses;
  }

  /**
   * Fair value refers to the actual value of a product
   * that is agreed upon by both the seller and the buyer.
   */
  static fairValue(_cost: number, _gain: number): number {
    const cost = Number(_cost ?? 0);
    const gain = Number(_gain ?? 0);
    return cost + gain;
  }

  /**
   * Distributed to Paid-In Capital (DPI) measure of the cumulative investment
   * returned to the investor relative to invested capital.
   */
  static dpi(_distribution: number, _contribution: number): number {
    const distribution = Number(_distribution ?? 0);
    const contribution = Number(_contribution ?? 0);
    return contribution ? distribution / contribution : 0;
  }

  /**
   * Residual Value to Paid-In (RVPI): measure of how much of the investors' capital
   * is still tied up in the equity of the fund.
   */
  static rvpi(_netAssetValue: number, _contribution: number): number {
    const netAssetValue = Number(_netAssetValue ?? 0);
    const contribution = Number(_contribution ?? 0);
    return contribution ? netAssetValue / contribution : 0;
  }

  /**
   * Net Asset Value (NAV) is the net value of an investment fund's assets less its liabilities,
   */
  static nav(_contribution: number, _distribution: number, _carryDistribution: number, _totalExpenses: number, _fairValue: number, _cost: number): number {
    const contribution = Number(_contribution ?? 0);
    const distribution = Number(_distribution ?? 0);
    const carryDistribution = Number(_carryDistribution ?? 0);
    const totalExpenses = Number(_totalExpenses ?? 0);
    const fairValue = Number(_fairValue ?? 0);
    const cost = Number(_cost ?? 0);
    return contribution + distribution + carryDistribution + totalExpenses + fairValue - cost;
  }

  /**
   * Net Asset Value (NAV) is the net value of an investment fund's assets less its liabilities,
   */
  static navWithGain(_contribution: number, _distribution: number, _carryDistribution: number, _totalExpenses: number, _gain: number): number {
    const contribution = Number(_contribution ?? 0);
    const distribution = Number(_distribution ?? 0);
    const carryDistribution = Number(_carryDistribution ?? 0);
    const totalExpenses = Number(_totalExpenses ?? 0);
    const gain = Number(_gain ?? 0);
    return contribution + distribution + carryDistribution + totalExpenses + gain;
  }

  /**
   * Carried interest, or carry, is a share of the profits of an investment paid to the investor
   */
  static carry(_netGain: number, _carryInterestPercent: number): number {
    const netGain = Number(_netGain ?? 0);
    const carryInterestPercent = Number(_carryInterestPercent ?? 0);
    return Math.abs(netGain * (carryInterestPercent / 100));
  }

  static asp(_arr: number, _clients: number): number {
    const arr = Number(_arr ?? 0);
    const clients = Number(_clients ?? 0);
    return clients ? arr / clients : 0;
  }

  static contributionPcnt(_contribution: number, _commitment: number): number {
    const contribution = Number(_contribution ?? 0);
    const commitment = Number(_commitment ?? 0);
    return commitment ? contribution / commitment : 0;
  }

  static tvpi(nav: number, distribution: number, contributionReceivedInAdvance: number, contribution: number,
    dueToCapitalContribution: number): number {
    const numerator = nav + Math.abs(distribution) - Math.abs(contributionReceivedInAdvance);
    const denominator = contribution - Math.abs(dueToCapitalContribution);
    if (denominator === 0) {
      return 0;
    }
    return Math.max(numerator / denominator, 0);
  }

  static absRatio(_value1: number, _value2: number): number {
    const value1 = Number(_value1 ?? 0);
    const value2 = Number(_value2 ?? 0);
    return value2 ? Math.abs(value1 / value2) : 0;
  }

  static ratio(_value1: number, _value2: number): number {
    const value1 = Number(_value1 ?? 0);
    const value2 = Number(_value2 ?? 0);
    return value2 ? value1 / value2 : 0;
  }

  static ratioInPcnt(_value1: number, _value2: number): number {
    const value1 = Number(_value1 ?? 0);
    const value2 = Number(_value2 ?? 0);
    return value2 ? (value1 / value2) * 100 : 0;
  }
}
